import type { KYCQuestionDefinition } from '@anchorage/graphql/generated';
import type { CustomLinkProps } from '@anchorage/utils';
import { isNotEmpty, parseSanitize } from '@anchorage/utils';
import type { ActionListOption } from '@latitude/action-list';
import { FormInput, FormSelect, reactHookForm } from '@latitude/form';
import { Icon } from '@latitude/icons';
import { Text } from '@latitude/text';
import type { ComponentType } from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';

import type { DropdownExtraData } from './helpers';
import { matchesDropdownSearch } from './helpers';

type Props = {
  isMulti?: boolean;
  question: KYCQuestionDefinition;
  isDisabled?: boolean;
  isBackoffice?: boolean;
  freeformOptionId?: string;
  CustomLink?: ComponentType<CustomLinkProps>;
  trackFn?: (questionId: string) => void;
};

function DropdownComponent({
  isMulti = false,
  question,
  isDisabled = false,
  isBackoffice = false,
  freeformOptionId = '',
  CustomLink,
  trackFn = () => {},
}: Props) {
  const { control, setValue, getFieldState } = reactHookForm.useFormContext();
  const [customOptionText, setCustomOptionText] = useState('');
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const selectedOption = reactHookForm.useWatch({
    control,
    name: `${question.id}.options`,
  }) as string[] | string;
  const { error } = getFieldState(`${question.id}.options`);

  const freeformOptionText = reactHookForm.useWatch({
    control,
    name: `${question.id}.freeformText`,
  }) as string;

  useEffect(() => {
    if (
      selectedOption !== freeformOptionId &&
      selectedOption?.[0] !== freeformOptionId
    ) {
      setCustomOptionText('');
      setValue(`${question.id}.freeformText`, '');
    }
  }, [
    selectedOption,
    freeformOptionId,
    question.id,
    setValue,
    setCustomOptionText,
  ]);

  useEffect(() => {
    if (freeformOptionText) {
      setCustomOptionText(freeformOptionText);
    }
  }, [freeformOptionText]);

  const handleInputChange = useCallback(
    (newValue?: string) => {
      newValue && setCustomOptionText(newValue);
    },
    [setCustomOptionText],
  );

  const finalOptions: ActionListOption<DropdownExtraData>[] = useMemo(() => {
    return [
      question?.options?.map(
        (option): ActionListOption<DropdownExtraData> => ({
          label: parseSanitize(option.text) as string,
          subtext: !isBackoffice
            ? (parseSanitize(option.subtext) as string)
            : undefined,
          value: option.id,
          extraData: { text: option.text, tag: option.tag },
        }),
      ),
      freeformOptionId && customOptionText
        ? {
            label: isMenuOpen ? `Use "${customOptionText}"` : customOptionText,
            leadingIcon:
              isMenuOpen && ((<Icon name="plus" />) as unknown as string),
            value: freeformOptionId,
            extraData: { text: customOptionText },
            onClick: () => {
              setValue(`${question.id}.freeformText`, customOptionText);
              setIsMenuOpen(false);
            },
          }
        : null,
    ]
      .flat()
      .filter(isNotEmpty);
  }, [
    customOptionText,
    isBackoffice,
    question?.options,
    freeformOptionId,
    setValue,
    question.id,
    isMenuOpen,
  ]);

  // track change
  const [userClicked, setUserClicked] = useState(false);
  const [prevValue, setPrevValue] = useState<string | string[] | undefined>('');
  useEffect(() => {
    setPrevValue(selectedOption);
    if (!userClicked) {
      return;
    }
    if (selectedOption !== prevValue) {
      trackFn?.(question.id);
    }
  }, [selectedOption, userClicked, question.id, prevValue, trackFn]);

  return (
    // We need this here because the scroll gets stuck inside dialogs
    <div
      onWheel={(event) => event.stopPropagation()}
      onClick={() => setUserClicked(true)}
    >
      <FormSelect
        truncateOptions
        readOnly={isDisabled}
        control={control}
        isMulti={isMulti}
        name={`${question.id}.options`}
        options={finalOptions.filter(isNotEmpty)}
        onSearchChange={handleInputChange}
        withSearch
        showSelectionElement
        onOpenChange={setIsMenuOpen}
        defaultSearchTerm={customOptionText}
        searchFn={matchesDropdownSearch}
      />
      {question?.footer && !error && (
        <Text className="mt-1" variant="captionMedium">
          {parseSanitize(question.footer, CustomLink)}
        </Text>
      )}
      {/** this is a special case: if a DROPDOWN has a freeformOptionId, we
       * want to allow the user to input its own option text. so we add another
       * input (hidden) to store the value of the custom option
       */}
      {freeformOptionId && (
        <div className="hidden">
          <FormInput
            type="hidden"
            control={control}
            name={`${question.id}.freeformText`}
          />
        </div>
      )}
    </div>
  );
}

export { DropdownComponent };
